import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { User, Headphones, Clock } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface GuidedSessionProps {
  onSelectSession: (session: GuidedSessionInfo) => void;
}

export interface GuidedSessionInfo {
  id: string;
  title: string;
  description: string;
  duration: number;
  frequency: number;
  voiceType: 'male' | 'female';
  category: 'meditation' | 'focus' | 'sleep';
  audioSrc?: string;  // In a real app, this would be the audio file path
}

// Sample guided sessions data
export const GUIDED_SESSIONS: GuidedSessionInfo[] = [
  {
    id: 'guided-meditation-1',
    title: 'Body Scan Meditation',
    description: 'A guided meditation focused on progressively relaxing each part of your body with theta waves.',
    duration: 15,
    frequency: 6,
    voiceType: 'female',
    category: 'meditation'
  },
  {
    id: 'guided-meditation-2',
    title: 'Breath Awareness',
    description: 'Focus on your breathing patterns with this alpha wave guided session for relaxation.',
    duration: 10,
    frequency: 10,
    voiceType: 'male',
    category: 'meditation'
  },
  {
    id: 'guided-focus-1',
    title: 'Deep Work Preparation',
    description: 'A guided session to prepare your mind for focused deep work with beta waves.',
    duration: 8,
    frequency: 15,
    voiceType: 'female',
    category: 'focus'
  },
  {
    id: 'guided-sleep-1',
    title: 'Sleep Journey',
    description: 'A gentle guided journey into deep sleep with delta waves.',
    duration: 20,
    frequency: 2.5,
    voiceType: 'male',
    category: 'sleep'
  },
  {
    id: 'guided-focus-2',
    title: 'Mindful Productivity',
    description: 'Enhance productivity and attention with this guided beta wave session.',
    duration: 12,
    frequency: 18,
    voiceType: 'female',
    category: 'focus'
  },
  {
    id: 'guided-sleep-2',
    title: 'Progressive Relaxation',
    description: 'Progressive muscle relaxation to help you fall asleep with theta to delta waves.',
    duration: 25,
    frequency: 4,
    voiceType: 'male',
    category: 'sleep'
  }
];

const GuidedSessions: React.FC<GuidedSessionProps> = ({ onSelectSession }) => {
  const [activeTab, setActiveTab] = useState<string>('all');
  
  const filteredSessions = activeTab === 'all' 
    ? GUIDED_SESSIONS 
    : GUIDED_SESSIONS.filter(session => session.category === activeTab);
  
  return (
    <div className="glass rounded-2xl p-6 animate-fade-in">
      <div className="flex items-center gap-2 mb-4">
        <Headphones size={18} className="text-primary" />
        <h3 className="text-lg font-medium">Guided Sessions</h3>
      </div>
      
      <p className="text-sm text-muted-foreground mb-4">
        Enhance your experience with voice-guided binaural beat sessions for specific purposes.
      </p>
      
      <Tabs defaultValue="all" className="w-full" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 mb-4">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="meditation">Meditation</TabsTrigger>
          <TabsTrigger value="focus">Focus</TabsTrigger>
          <TabsTrigger value="sleep">Sleep</TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="space-y-4 mt-0">
          {filteredSessions.map(session => (
            <div 
              key={session.id}
              className="border border-border hover:border-primary/30 rounded-lg p-4 cursor-pointer transition-colors"
              onClick={() => onSelectSession(session)}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium">{session.title}</h4>
                  <p className="text-xs text-muted-foreground">{session.description}</p>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-xs font-medium">{session.duration} min</span>
                  <span className="text-xs text-muted-foreground">{session.frequency} Hz</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center mt-4">
                <div className="flex items-center text-xs text-muted-foreground">
                  <User size={14} className="mr-1" />
                  {session.voiceType === 'male' ? 'Male' : 'Female'} Voice
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="h-7 text-xs"
                >
                  Play Session
                </Button>
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GuidedSessions;
